/**
 * Translation frontend manager.
 * Usage:
 * 1) "use JsLanguageSetter;" in your controller
 * 2) "$this->setLanguage('media/global', 'main/global.yes', 'some/other.namespace');"
 * 3) Use in frontend using the two functions below - trans() and trans_choice() - slashes not accepted, replace them
 * with dots. trans('main/global.yes') in the backend would be trans('main.global.yes') in the frontend.
 *
 * For vue - methods are scoped, so whenever you need it, link it inside your component.
 * data: function() {
 *      return {
 *          trans: window.trans
 *      };
 * }
 *
 * ...after that you just use it in template same as you would in blades: {{ trans('main.global.yes') }}
 *
 * Note: this is not exposed. Expose to window if you need to access the base singleton.
 * @author Kriss Kulins
 */
const Localization = {

    /**
     * Sort replacements by length to make sure that "var" isn't replaced before "variable", thus breaking the string.
     * @param replacements object where key is variable name and value is it's value
     * @returns {Array}
     */
    sortReplacements: function (replacements) {
        let sortedReplacements = [];
        for (let i in replacements) {
            sortedReplacements.push([i, replacements[i]]);
        }
        sortedReplacements.sort((a, b) => {
            return a[0].length < b[0].length;
        });

        return sortedReplacements;
    },

    /**
     * Retrieve language string from Javascript stash.
     * @param key string separated exclusively by dots instead of slashes to separate files
     * @returns {Localization.trans|*}
     */
    getLanguageString: function (key) {
        key = key.split('.');

        let languageString = false;
        let transObj = Regulator.trans;
        let langObject = Regulator.lang;

        if (transObj) {
            languageString = this.findStringInObject(key, transObj);
        }

        if (typeof (languageString) === 'string') {
            return languageString;
        }

        if (langObject) {
            languageString = this.findStringInObject(key, langObject);
        }

        if (typeof (languageString) === 'string') {
            return languageString;
        }

        return key;
    },

    /**
     * Find language string by key in given object
     * @param key string, language object
     * @returns {Localization.trans}
     */
    findStringInObject: function (key, object) {
        let languageString = object;

        for (let i in key) {
            if (typeof (languageString[key[i]]) !== 'undefined') {
                languageString = languageString[key[i]];
            } else {
                break;
            }
        }

        return languageString;
    },

    /**
     * Replace variables in the given language string.
     * @param languageString string language string
     * @param sortedReplacements array of arrays where first element of each one is the variable name and second is value.
     * @returns {*}
     */
    replaceVariables: function (languageString, sortedReplacements) {
        if (typeof languageString === 'string' && sortedReplacements.length) {
            for (let i in sortedReplacements) {
                languageString = languageString.replace(
                    new RegExp('\:' + sortedReplacements[i][0] + '(\s||^A-Za-z0-9)', 'g'), sortedReplacements[i][1]
                );
            }
        }

        return languageString;
    },

    /**
     * Translate language string and replace variables.
     * @param key string separated exclusively by dots instead of slashes to separate files
     * @param replacements object where key is variable name and value is it's value
     * @returns string
     */
    trans: function (key, replacements) {
        if (!Regulator.trans && !Regulator.lang) {
            console.warn('trans: language string requested, when none received from backend');
            return key;
        }

        let sortedReplacements = [];
        if (replacements) {
            sortedReplacements = this.sortReplacements(replacements);
        }

        let languageString = this.getLanguageString(key);

        let translated = this.replaceVariables(languageString, sortedReplacements);
        if (typeof translated !== 'string') {
            translated = key;
        }

        return translated;
    },

    /**
     * Translate language string, get correct pluralization part and replace variables.
     * @param key string separated exclusively by dots instead of slashes to separate files
     * @param count integer count to compare against pluralization rules
     * @param replacements object where key is variable name and value is it's value
     * @returns string
     */
    transChoice: function (key, count, replacements) {
        if (!Regulator.trans && !Regulator.lang) {
            console.warn('trans: language string requested, when none received from backend');
            return key;
        }

        let sortedReplacements = [];
        if (replacements) {
            sortedReplacements = this.sortReplacements(replacements);
        }

        let languageString = this.getLanguageString(key);

        // If language string getter returned an array / object, we're not interested...
        if (typeof languageString === 'string') {
            let ranges = [];
            let languageStringParts = languageString.split('|');

            // Loop through all separated parts.
            if (languageStringParts.length > 1) {
                for (let i in languageStringParts) {

                    // Find ranges in parts - example: [0,10]
                    // Regex translation if you find any bugs:
                    // (^|\|) - before the rules there is either start of string or separator (|)
                    // \[ - ranges start with a square bracket
                    // ([0-9]+|\*) - low value contains one or more numbers (multi digits allowed) or an asterisk for "any"
                    // , - separates the high and low values in the range
                    // ([0-9]+|\*) - high value of range, same as low
                    // ] - range closing bracket
                    // \s - should always be followed by a whitespace before the next character
                    let rangeMatches = languageStringParts[i].match(/(^|\|)\[([0-9]+|\*),([0-9]+|\*)]\s/g);
                    for (let j in rangeMatches) {
                        let match = rangeMatches[j].split(',');
                        let lowValue = trimString(match[0].replace('[', ''), ' ');
                        let highValue = trimString(match[1].replace(']', ''), ' ');
                        ranges.push({
                            type: 'range',
                            low: lowValue !== '*' ? parseInt(lowValue) : lowValue,
                            high: highValue !== '*' ? parseInt(highValue) : highValue,
                            replacement: rangeMatches[j],
                            stringPart: languageStringParts[i]
                        });
                    }

                    // Find match clauses - example: {0}
                    let preciseMatches = languageStringParts[i].match(/{[0-9]+}\s/g);
                    for (let j in preciseMatches) {
                        let match = preciseMatches[j].replace('{', '');
                        match = match.replace('}', '');
                        ranges.push({
                            type: 'match',
                            high: parseInt(match),
                            replacement: preciseMatches[j],
                            stringPart: languageStringParts[i]
                        });
                    }
                }
            }

            if (ranges.length > 0) {
                // Set this by default, in case there are no matches.
                languageString = key;
                for (let i in ranges) {
                    if (ranges[i].type === 'match' && count === ranges[i].high) {
                        languageString = ranges[i].stringPart.replace(ranges[i].replacement, '');
                        break;
                    } else if (ranges[i].type === 'range' &&
                        (ranges[i].high > count || ranges[i].high === '*') &&
                        (ranges[i].low <= count || ranges[i].low === '*')
                    ) {
                        languageString = ranges[i].stringPart.replace(ranges[i].replacement, '');
                        break;
                    }
                }
            }
        } else {
            languageString = key;
        }

        if (languageString !== key) {
            languageString = this.replaceVariables(languageString, sortedReplacements);
        }

        return languageString;
    }
};

/**
 * Simple wrapper function for Localization class method.
 * @param key string separated exclusively by dots instead of slashes to separate files
 * @param replacements object where key is variable name and value is it's value
 * @returns string
 */
window.trans = function (key, replacements) {
    return Localization.trans(key, replacements);
};

/**
 * Translate language string, get correct pluralization part and replace variables.
 * @param key string separated exclusively by dots instead of slashes to separate files
 * @param count integer count to compare against pluralization rules
 * @param replacements object where key is variable name and value is it's value
 * @returns string
 */
window.trans_choice = function (key, count, replacements) {
    return Localization.transChoice(key, count, replacements);
};

export default {
    install(Vue, options) {
        Vue.prototype.trans_choice = (key, count, replacements) => {
            return Localization.transChoice(key, count, replacements);
        };

        Vue.prototype.trans = (key, replacements) => {
            return Localization.trans(key, replacements);
        };
    }
};